import {useEffect, useState} from "react";
import {useHistory} from "react-router-dom";
import {
    Button,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    Stack
} from "@chakra-ui/react";
import {useFeedback} from "../store/feedback";
import {useCreatePost} from "../api/submit";
import ImageField from "./image_field";
import MarkdownField from "./markdown_field";
import styled from "styled-components/macro";
import {AutoResizeTextarea} from "./auto_resize_textarea";
import {useBaseCommunity} from "../store/hooks";
import {isPseudoCommunity} from "../store/communites";
import {debounce} from "lodash";

const Title = styled.div`
  display: flex;

  label {
    flex-grow: 1;
  }

  span {
    font-size: 13px;
  }`;

const postFieldWhitelistByType = {
    media: ['community', 'title', 'link'],
    text: ['community', 'title', 'content'],
    link: ['community', 'title', 'link'],
};
const filterPostFields = (post) => {
    const result = {};
    result['type'] = (post.type === 'text') ? 'text' : 'link';
    for (const name of postFieldWhitelistByType[post.type]) {
        result[name] = post[name];
    }
    return result;
};

const CreatePost = ({community, onClose}) => {
    const {error, feedbackCh} = useFeedback();
    const history = useHistory();
    const [post, setPost] = useState({
        type: 'media',
        community,
        title: '',
        link: '',
    });
    const set = (name) => (value) => setPost((post) => ({...post, [name]: value}));
    const [posting, setPosting] = useState(false);

    const createPost = useCreatePost(feedbackCh);
    const handleSave = async () => {
        if (posting) return;
        try {
            setPosting(true);
            const {link} = await createPost(filterPostFields(post));
            onClose();
            history.replace(link);
        } catch {
            // error is displayed
        }
        setPosting(false);
    };

    const [paste, setPaste] = useState(false);
    const handlePaste = (e) =>{
        if (e.clipboardData.files.length) {
            setPaste(e.clipboardData.files[0]);
        }
    }

    const titleLength = 300 - post.title.length;
    const baseCommunity = useBaseCommunity();

    const isNotPolitical = false;

    useEffect(() => {
        const debouncedFunction = debounce(() => {
            console.log("community updated");
        }, 300);

        debouncedFunction();

        return () => {
            debouncedFunction.cancel();
        };
    }, [post.community]);

    return (
        <>
            <Modal
                isOpen
                onClose={onClose}
                closeOnOverlayClick={false}
                size={'xl'}
            >
                <ModalOverlay />
                <ModalContent onPaste={handlePaste}>
                    <ModalHeader>Create a post</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>

                        <Stack spacing={'5'}>

                        <FormControl>
                            <Title>
                                <FormLabel>Title</FormLabel>
                                <span style={{color: titleLength < 20 ? '#ff0000' : '#c1c1c1'}}>{titleLength}</span>
                            </Title>
                            <AutoResizeTextarea placeholder="" value={post.title} maxLength={300} rows={1}
                                                onChange={(e) => set('title')(e.currentTarget.value)}/>
                            <FormHelperText>This cannot be edited later.</FormHelperText>
                        </FormControl>

                            {isPseudoCommunity(baseCommunity) &&
                                <FormControl>
                                    <FormLabel>Community</FormLabel>
                                    <Input placeholder="" value={post.community}
                                           onChange={(e) => set('community')(e.currentTarget.value)}/>
                                    {isNotPolitical &&
                                        <FormHelperText style={{color: 'red'}}>This community does not allow discussion of
                                            politics or social issues.</FormHelperText>
                                    }
                                </FormControl>
                            }

                            {post.type === 'text' && (
                                <FormControl>
                                    <FormLabel>Content</FormLabel>
                                    <MarkdownField form value={post.content} rows={5} onChange={set('content')}/>
                                </FormControl>
                            )}
                            {post.type === 'link' && (
                                <FormControl>
                                    <FormLabel>Link</FormLabel>
                                    <Input type="link"
                                           value={post.link}
                                           placeholder={"https://"}
                                           onChange={(e) => set('link')(e.currentTarget.value)} />
                                    <FormHelperText>Secure links only!</FormHelperText>
                                </FormControl>
                            )}

                            {post.type === 'media' && (
                                <FormControl>
                                    <FormLabel>Media</FormLabel>
                                    <ImageField
                                        paste={paste}
                                        setPaste={setPaste}
                                        id='link'
                                        value={post.link}
                                        onChange={set('link')}
                                        required
                                    />
                                </FormControl>
                            )}

                        <FormControl>
                            <FormLabel>Post Type</FormLabel>
                            <Select value={post.type} onChange={(e) => set('type')(e.currentTarget.value)}>
                                <option value="media">Media Post</option>
                                <option value="text">Text Post</option>
                                <option value="link">Link Post</option>
                            </Select>
                        </FormControl>

                        <FormControl isInvalid={error}>
                            <FormErrorMessage>{error?.toString()}</FormErrorMessage>
                        </FormControl>

                        </Stack>

                    </ModalBody>

                    <ModalFooter>
                        <Button variant="cancel" mr={3} onClick={onClose}>Cancel</Button>
                        <Button variant="blue" isLoading={posting} onClick={handleSave}>
                            Post
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    )
};

export default CreatePost;
